import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Search, X } from 'lucide-react'
import { IconButton, Input } from '@/components/ui'
import { useArticles } from '../api/contractsApi.ts'

interface SapArticlePickerProps {
  label?: ReactNode
  hint?: ReactNode
  placeholder?: string
  /** Aktuell gesetzter SAP ItemCode. */
  value: string
  /** Wird mit dem neuen ItemCode (oder leerem String) aufgerufen, plus Snapshot-Name. */
  onChange: (itemCode: string, name?: string) => void
  /** Initiale Anzeige (z. B. wenn Produkt bereits einen Artikel referenziert). */
  initialName?: string
}

/**
 * Picker fuer SAP-Artikel. SAP ist fuehrend; gesucht wird in der lokalen
 * `articles`-Tabelle (ueber /api/articles?q=…). Der Cron `sap-article-sync`
 * haelt den lokalen Stamm aktuell, sodass keine Live-SAP-Last entsteht.
 *
 * Wird in `ProductFormModal` fuer die Bruecke product_catalog → articles
 * verwendet (Etappe 1.5).
 */
export function SapArticlePicker({
  label,
  hint,
  placeholder = 'SAP-Artikel suchen (Code, Name oder Hersteller) …',
  value,
  onChange,
  initialName,
}: SapArticlePickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [selectedName, setSelectedName] = useState<string | null>(initialName ?? null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const { data, isLoading } = useArticles(query)
  const articles = data?.data ?? []

  useEffect(() => {
    if (initialName !== undefined) setSelectedName(initialName ?? null)
  }, [initialName])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  function clearSelection() {
    setSelectedName(null)
    setQuery('')
    onChange('', undefined)
  }

  if (value) {
    return (
      <div className="space-y-1">
        {label ? <p className="text-small font-medium text-gray-700">{label}</p> : null}
        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
          <span className="text-small font-medium text-gray-900">{selectedName ?? value}</span>
          <span className="text-caption text-gray-500">({value})</span>
          <span className="ml-auto" />
          <IconButton
            icon={<X className="w-3.5 h-3.5" />}
            aria-label="Auswahl entfernen"
            variant="ghost"
            size="sm"
            onClick={clearSelection}
          />
        </div>
        {hint ? <p className="text-caption text-gray-500">{hint}</p> : null}
      </div>
    )
  }

  return (
    <div className="space-y-1" ref={containerRef}>
      <div className="relative">
        <Input
          label={label}
          hint={hint}
          placeholder={placeholder}
          leadingIcon={<Search className="w-4 h-4" />}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.currentTarget.value)
            setOpen(true)
          }}
        />
        {open && query.length >= 2 ? (
          <div className="absolute left-0 right-0 top-full z-popover mt-1 max-h-72 overflow-auto rounded-md border border-gray-200 bg-white shadow-popover">
            {isLoading ? (
              <p className="px-3 py-2 text-caption text-gray-500">Suche …</p>
            ) : articles.length === 0 ? (
              <p className="px-3 py-2 text-caption text-gray-500">Keine Treffer.</p>
            ) : (
              <ul className="py-1">
                {articles.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-small hover:bg-gray-50"
                      onClick={() => {
                        onChange(a.itemCode, a.itemName)
                        setSelectedName(a.itemName)
                        setQuery('')
                        setOpen(false)
                      }}
                    >
                      <span className="font-medium text-gray-900">{a.itemName}</span>
                      <span className="text-caption text-gray-500">{a.itemCode}</span>
                      {a.manufacturer ? (
                        <span className="text-caption text-gray-400">· {a.manufacturer}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
